import { isNegative, normalizeVoiceText } from "../../voice/ownerVoiceParsers";
import { ownerJarvisConversationContext } from "../ownerJarvisConversationJournal";
import { readOwnerJarvisStoredTurn } from "../ownerJarvisStorage";

const SECTION_LABELS = Object.freeze({
  "today-jarvis": "Jarvis / Hoy",
  "today-agenda": "Agenda de hoy",
  "today-money": "Dinero de hoy",
  "project-summary": "Resumen del proyecto",
  "project-progress": "Avance del proyecto",
  "project-steps": "Pendientes / pasos del proyecto",
  "project-economics": "Economía del proyecto",
  "project-agreement": "Acuerdo del proyecto",
  "project-money": "Cobros del proyecto",
  "project-billing": "Billing WebProfit",
  "project-payments": "Próximos cobros",
  "project-work": "Trabajo del proyecto",
  "project-webprofit": "WebProfit vinculado",
  "project-people": "Cliente / personas del proyecto",
  "jarvis-lab": "Jarvis Lab",
});

function rawText(input = {}) {
  return String(input.raw || input.text || "").trim();
}

const PENDING_CAPTURE_TTL_MS = 3 * 60 * 1000;

function pendingCapturePrompt() {
  return "Sí, lo anoto. Terminame la idea: ¿qué querés que prepare o qué debería pasar?";
}

function isClearlyIncompleteBacklogReport(value) {
  const text = normalizeVoiceText(value);
  if (!text) return true;

  const withoutCaptureIntent = text
    .replace(/\b(?:jarvis|quiero|necesito|podes|podes|podrias|podria|que|me|por favor)\b/g, " ")
    .replace(/\b(?:anota|anotar|anotalo|anotame|registra|registrar|registralo|carga|cargar|cargalo|agrega|agregar|agregalo|mete|meter|metelo|guarda|guardar|guardalo)\b/g, " ")
    .replace(/\b(?:un|una|el|la|los|las|este|esta|en|al|del)\b/g, " ")
    .replace(/\b(?:error|errores|bug|bugs|falla|fallas|fallo|fallos|problema|problemas|backlog|fix list|fixlist|fix|fixes|mejora|mejoras)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!withoutCaptureIntent || /^(?:si\s+)?(?:diciendo\s+)?(?:de\s+)?que$/.test(withoutCaptureIntent)) return true;

  if (/\b(?:diciendo(?: de)? que|de que|para que|porque|cuando|donde|como|y que|quiero que|necesito que)\s*$/.test(text)) {
    return true;
  }

  if (/\b(?:de|para|con|como|porque|cuando|donde|que|y)\s*$/.test(text)) return true;

  // Un pedido que sólo nombra un contenedor genérico todavía no define qué debe
  // resolver ese apartado/sección. Es el caso de dictado truncado que originó ROW-15.
  if (/\b(?:vaya\s+)?(?:preparando|preparar|prepare|armando|armar|arme|haciendo|hacer|haga|creando|crear|cree|agregando|agregar|agregue)\s+(?:un|una|el|la)?\s*(?:apartado|seccion|parte|cosa|tema)\s*$/.test(text)) {
    return true;
  }

  return false;
}

function isClearBacklogCaptureCancellation(value) {
  const text = normalizeVoiceText(value);
  if (!text) return false;
  if (isNegative(text)) return true;
  return /^(?:dejalo|dejala|olvidalo|olvidala|no lo anotes|no la anotes|cancela eso|cambiar de tema|cambiemos de tema)\b/.test(text);
}

function isClearContextSwitch(value) {
  const text = normalizeVoiceText(value);
  if (!text) return false;
  return /^(?:abri|abre|abrime|mostra|mostrame|vamos a|anda a|andemos a|ir a|quiero ir a|llevame a|volver a|volvamos a)\b/.test(text)
    || /^(?:quiero|vamos a|necesito)\s+(?:crear|agendar|registrar|anotar)\s+(?:un|una)\s+(?:habito|tarea|gasto|persona|proyecto|rutina|cobro|pago)\b/.test(text);
}

function pendingCaptureExpired(pending) {
  return !pending?.startedAt || Date.now() - Number(pending.startedAt) > PENDING_CAPTURE_TTL_MS;
}

function combinedBacklogDescription(first, continuation) {
  return `${String(first || "").trim()}\n\nCONTINUACIÓN DEL OWNER:\n${String(continuation || "").trim()}`.trim();
}

function explicitExternalId(value) {
  const match = String(value || "").toUpperCase().match(/\b(?:MEJ|FIX)[-\s]?(\d{1,6})\b/);
  return match ? `MEJ-${String(Number(match[1])).padStart(3, "0")}` : "";
}

function visibleOwnerSection() {
  if (typeof document === "undefined") return null;
  const rows = Array.from(document.querySelectorAll("[data-owner-jarvis-section], [data-owner-project-section]"))
    .map((node) => {
      const rect = node.getBoundingClientRect();
      const key = node.getAttribute("data-owner-jarvis-section") || node.getAttribute("data-owner-project-section") || "";
      const visible = rect.bottom > 70 && rect.top < window.innerHeight && rect.width > 0 && rect.height > 0;
      return { key, rect, visible, depth: key.split("-").length };
    })
    .filter((row) => row.visible && row.key)
    .sort((a, b) => Math.abs(a.rect.top - 150) - Math.abs(b.rect.top - 150) || b.depth - a.depth);
  return rows[0]?.key || null;
}

function sourceContext(input = {}) {
  const route = String(input.routeContext || (typeof window !== "undefined" ? `${window.location.pathname || ""}${window.location.search || ""}` : ""));
  const activeTurn = readOwnerJarvisStoredTurn();
  const activeSession = activeTurn?.session && typeof activeTurn.session === "object" ? activeTurn.session : null;
  const functional = activeTurn?.functionalContext && typeof activeTurn.functionalContext === "object" ? activeTurn.functionalContext : null;

  if (activeSession && functional?.label) {
    return {
      route,
      surface: "owner_jarvis",
      section: String(activeSession.kind || "jarvis"),
      sectionLabel: String(functional.label || ""),
      functionalPath: Array.isArray(functional.segments) ? functional.segments.slice(0, 4) : [],
      entityType: String(functional.entityType || ""),
      entityId: String(functional.entityId || ""),
      entityLabel: String(functional.nodeLabel || ""),
      initiativeId: String(functional.initiativeId || activeSession.initiativeId || ""),
      source: input.inputSource || "jarvis",
    };
  }

  const section = visibleOwnerSection() || "";
  const projectMatch = route.match(/\/dashboard\/owner\/proyectos\/([^/?#]+)/);
  return {
    route,
    surface: projectMatch ? "project360" : route.includes("/jarvis-lab") ? "jarvis_lab" : "owner",
    section,
    sectionLabel: SECTION_LABELS[section] || "",
    entityType: projectMatch ? "project" : "",
    entityId: projectMatch ? decodeURIComponent(projectMatch[1]) : "",
    initiativeId: projectMatch ? decodeURIComponent(projectMatch[1]) : "",
    source: input.inputSource || "jarvis",
  };
}

function priorityFromText(value) {
  const text = normalizeVoiceText(value);
  if (/\b(critico|critica|urgente|grave|bloquea|rompe|error|bug|falla)\b/.test(text)) return "ALTA";
  if (/\b(baja|menor|detalle|cuando puedas)\b/.test(text)) return "BAJA";
  return "MEDIA";
}

function titleFromUtterance(value) {
  const raw = String(value || "").trim().replace(/\s+/g, " ");
  const stripped = raw
    .replace(/^jarvis[,\s:]*/i, "")
    .replace(/^(?:anota|anotá|registra|registrá|carga|cargá|agrega|agregá)\s+(?:un|una|este|esta)?\s*(?:error|bug|fix|mejora)?\s*[:,-]?\s*/i, "")
    .trim();
  const candidate = stripped || raw;
  return candidate.length > 150 ? `${candidate.slice(0, 147)}...` : candidate;
}

function isOwnerJarvisBacklogNavigationRequest(value) {
  const text = normalizeVoiceText(value);
  if (!text) return false;
  if (/^(?:backlog|fixes|mejoras|errores)$/.test(text)) return true;
  if (/^jarvis lab$/.test(text)) return true;
  if (/\b(?:lista de|listar|ver|mostrar|mostrame|abrir|abre|abri|ir al|ir a)\s+(?:(?:el|la|los|las)\s+)?(?:jarvis lab|backlog|fixes|mejoras|errores)\b/.test(text)) return true;
  if (/^(?:que|cuales)\s+(?:errores|fixes|mejoras)\b/.test(text)) return true;
  return false;
}

function wantsBacklog(value) {
  return isOwnerJarvisBacklogNavigationRequest(value);
}

function startsLikeNewInstruction(value) {
  const text = normalizeVoiceText(value);
  if (!text) return false;
  return /^(?:ahora\s+)?(?:anot\w*|carg\w*|registr\w*|met\w*|agreg\w*|guard\w*|quiero|necesito|podes|podrias|dej\w*|abr\w*|mostr\w*|and\w*|vamos|vincul\w*|crea\w*|crear|hac\w*)\b/.test(text);
}

function acknowledgementFollowUpRaw(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const connector = raw.match(/\b(?:ahora|adem[aá]s|tambi[eé]n)\b[\s,:;.-]*(.+)$/i);
  if (connector?.[1] && startsLikeNewInstruction(connector[1])) return connector[1].trim();

  const punctuated = raw.match(/[;,.!?]\s*(.+)$/);
  if (punctuated?.[1] && startsLikeNewInstruction(punctuated[1])) return punctuated[1].trim();

  const coordinated = raw.match(/\by\s+(.+)$/i);
  if (coordinated?.[1] && startsLikeNewInstruction(coordinated[1])) return coordinated[1].trim();

  return "";
}

function parseBacklogAcknowledgement(value) {
  const raw = String(value || "").trim();
  const text = normalizeVoiceText(raw);
  if (!text) return null;

  const thanks = /^(?:(?:listo|perfecto|genial|buenisimo|ok|dale)\s+)?(?:muchas\s+)?gracias(?:\s+jarvis)?\b/.test(text);
  if (!thanks) return null;

  const acknowledgement = /^(?:(?:listo|perfecto|genial|buenisimo|ok|dale)\s+)?(?:muchas\s+)?gracias(?:\s+jarvis)?\s+por\s+(?:(?:haber|ya)\s+)?(?:anot\w*|carg\w*|registr\w*|met\w*|agreg\w*|guard\w*)\b/.test(text);
  const feedback = /\b(?:sigue|siguen|seguia|continua|todavia)\s+(?:mal|fallando|sin funcionar)|\bno\s+(?:funciona|quedo|esta resuelto|se resolvio)|\b(?:falla|fallo|rompio|falta|faltan)\b/.test(text);
  const priorBacklogReference = /\b(?:ese|esa|este|esta|el|la)\s+(?:error|problema|bug|falla|mejora|fix)\b/.test(text);
  if (feedback && (acknowledgement || priorBacklogReference)) return { kind: "feedback", followUpRaw: "" };

  const followUpRaw = acknowledgementFollowUpRaw(raw);
  if (followUpRaw) return { kind: "new_request", followUpRaw };

  return acknowledgement ? { kind: "pure", followUpRaw: "" } : null;
}

export function isOwnerJarvisBacklogAcknowledgement(value) {
  return parseBacklogAcknowledgement(value)?.kind === "pure";
}

function backlogCapturePayload(value) {
  const acknowledgement = parseBacklogAcknowledgement(value);
  if (!acknowledgement) return String(value || "").trim();
  if (acknowledgement.kind === "new_request") return acknowledgement.followUpRaw;
  return "";
}

export function isExplicitOwnerJarvisBacklogCaptureRequest(value) {
  const acknowledgement = parseBacklogAcknowledgement(value);
  if (acknowledgement?.kind === "pure" || acknowledgement?.kind === "feedback") return false;

  const candidate = acknowledgement?.kind === "new_request" ? acknowledgement.followUpRaw : value;
  const text = normalizeVoiceText(candidate);
  if (!text || explicitExternalId(candidate) || isOwnerJarvisBacklogNavigationRequest(candidate)) return false;
  const issue = /\b(error|errores|bug|bugs|falla|fallas|fallo|fallos|problema|problemas|rompio|no funciona|no entendio|se trabo)\b/.test(text);
  const backlog = /\b(backlog|fix list|fixlist|fix|fixes|mejora|mejoras)\b/.test(text);
  const captureVerb = /\b(anota|anotalo|registra|carga|agrega|mete|metelo|activa|guarda)\b/.test(text);
  // Las palabras de producto vuelven a ser marcadores de captura, no comandos
  // que el usuario tenga que formular de una manera rígida. Sólo las órdenes
  // inequívocas de navegación ("abrí el backlog", "mostrame los errores")
  // quedan afuera. La descripción completa se conserva al crear el item.
  if (backlog || issue) return true;
  if (captureVerb && /\b(producto|webprofit|jarvis)\b/.test(text)) return true;
  // Si el turno empezó agradeciendo una captura ya hecha, "anotá otro" conserva
  // ese contexto de backlog sin volver a persistir el agradecimiento anterior.
  if (acknowledgement?.kind === "new_request" && captureVerb && /\b(?:otro|otra|uno mas|una mas|nuevo|nueva)\b/.test(text)) return true;
  return false;
}

export function isClearOwnerJarvisBacklogCaptureCommand(value) {
  const text = normalizeVoiceText(value);
  if (!text) return false;
  const captureVerb = /\b(anota|anotar|anotalo|anotame|registra|registrar|registrame|carga|cargar|cargame|agrega|agregar|agregame|mete|meter|metelo|guarda|guardar|guardame)\b/.test(text);
  const captureTarget = /\b(error|errores|bug|bugs|falla|fallas|fallo|fallos|problema|problemas|backlog|fix|fixes|mejora|mejoras|producto|webprofit|jarvis)\b/.test(text);
  return captureVerb && captureTarget;
}

function looksLikeNewIssue(value) {
  const text = normalizeVoiceText(value);
  if (!text || explicitExternalId(value)) return false;
  if (isExplicitOwnerJarvisBacklogCaptureRequest(value)) return true;
  if (/\b(anota|registra|carga|agrega)\b/.test(text) && /\b(error|bug|fix|mejora|problema)\b/.test(text)) return true;
  if (/\bjarvis\b/.test(text) && /\b(deberia|tendria que|no hace|no esta haciendo|falla|error|mejora)\b/.test(text)) return true;
  if (/\b(aca|aqui|esta vista|este panel|esto)\b/.test(text) && /\b(error|falla|no funciona|deberia|mejora|rompe)\b/.test(text)) return true;
  if (/\b(aca|aqui|esto|esta vista|este panel|jarvis|sistema|herramienta)\b.*\b(deberia poder|tendria que poder|le falta|no tiene|estaria bueno)\b/.test(text)) return true;
  if (/\b(estoy pensando que|estoy pensando en|me gustaria|quisiera)\b.*\b(jarvis|sistema|panel|aca|aqui|esto|herramienta)\b/.test(text)) return true;
  if (/\b(quiero que|necesito que|haria falta que|estaria bueno que)\b.*\b(hagas|haga|agregues|agregue|crees|cree|permita|pueda|tenga|muestre|vincule|cierre|detecte|guarde|anote)\b/.test(text)) return true;
  return false;
}

export function ownerJarvisImprovementReviewDecision(value) {
  const text = normalizeVoiceText(value);
  if (!text) return "";
  if (text === "review_bad" || /\b(quedo mal|sigue mal|no quedo bien|no esta bien|no funciona|fallo|falla|error|todavia falla|todavia esta mal|todavia no funciona|todavia no quedo bien|todavia falta|falta|rompio)\b/.test(text)) return "negative";
  if (text === "review_later" || /\b(despues|mas tarde|luego|ahora no)\b/.test(text)) return "later";
  if (text === "review_ok" || /\b(quedo bien|esta bien|funciona bien|funciona|solucionado|resuelto|perfecto)\b/.test(text)) return "positive";
  return "";
}

export function createOwnerJarvisImprovementCapability({ ownerApi, navigate }) {
  let pendingCapture = null;

  const clearPendingCapture = () => {
    pendingCapture = null;
  };

  return {
    id: "owner-improvement-tracker",
    hasPendingInteraction() {
      return Boolean(pendingCapture && !pendingCaptureExpired(pendingCapture));
    },
    resetPendingInteraction() {
      clearPendingCapture();
    },
    async handle(input = {}) {
      const raw = rawText(input);
      if (!raw) return null;

      if (pendingCapture && pendingCaptureExpired(pendingCapture)) clearPendingCapture();

      if (pendingCapture) {
        if (isClearBacklogCaptureCancellation(raw)) {
          clearPendingCapture();
          return {
            handled: true,
            message: "Listo, no lo anoto.",
            highlightSection: "today-jarvis",
          };
        }

        if (isOwnerJarvisBacklogNavigationRequest(raw) || isClearContextSwitch(raw)) {
          clearPendingCapture();
          // Soltamos la mini conversación y dejamos que esta misma frase continúe
          // por el router normal. No se crea una fila residual.
          if (!isOwnerJarvisBacklogNavigationRequest(raw)) return null;
        } else if (isClearlyIncompleteBacklogReport(raw)) {
          return {
            handled: true,
            message: pendingCapturePrompt(),
            highlightSection: pendingCapture.context?.section || "today-jarvis",
          };
        } else {
          const original = pendingCapture;
          clearPendingCapture();

          const context = original.context;
          const description = combinedBacklogDescription(original.originalMessage, raw);
          const response = await ownerApi.createJarvisBacklogItem({
            priority: priorityFromText(description),
            title: titleFromUtterance(description),
            description,
            humanNote: `Capturado por Jarvis desde ${original.locationText}.`,
            sourceContext: context,
            conversationContext: ownerJarvisConversationContext(),
            captureKind: /\b(error|bug|falla|rompe|no funciona)\b/.test(normalizeVoiceText(description)) ? "EXPLICIT_ISSUE" : "FEATURE_IDEA",
          });
          const item = response?.item || {};
          const label = item.externalId || item.localRef || "el fix local";
          const pendingSync = item?.sync?.state !== "synced";
          return {
            handled: true,
            message: pendingSync
              ? `Anoté ${label} desde ${original.locationText}. Quedó guardado localmente y pendiente de sincronizar con Google Sheets; no se pierde aunque la conexión esté caída.`
              : `Anoté ${label} desde ${original.locationText} y ya quedó sincronizado con Google Sheets.`,
            highlightSection: context.section || "today-jarvis",
          };
        }
      }

      const explicitCapture = isExplicitOwnerJarvisBacklogCaptureRequest(raw);
      const capturePayload = explicitCapture ? (backlogCapturePayload(raw) || raw) : raw;
      if (!explicitCapture && wantsBacklog(raw)) {
        navigate("/dashboard/owner/jarvis-lab");
        return { handled: true, message: "Te abro Jarvis Lab. Ahí están la conexión con Google Sheets y los fixes que Jarvis fue capturando." };
      }

      const externalId = explicitExternalId(raw);
      if (externalId) {
        const decision = ownerJarvisImprovementReviewDecision(raw);
        if (!["negative", "positive"].includes(decision)) return null;
        const negative = decision === "negative";
        const item = await ownerApi.reviewJarvisBacklogItem(externalId, { accepted: !negative, note: raw });
        return {
          handled: true,
          message: negative
            ? `${externalId} vuelve a ACTIVO y agregué tu devolución sin borrar el resultado ni los intentos anteriores.`
            : `${externalId} queda SOLUCIONADO con tu validación.`,
          highlightSection: "today-jarvis",
          item: item?.item || item,
        };
      }

      if (!looksLikeNewIssue(raw)) return null;
      const context = sourceContext(input);
      const locationText = Array.isArray(context.functionalPath) && context.functionalPath.length
        ? context.functionalPath.join(" → ")
        : context.sectionLabel || (context.surface === "project360" ? "la ficha del proyecto" : "Owner");

      if (explicitCapture && isClearlyIncompleteBacklogReport(capturePayload)) {
        pendingCapture = {
          originalMessage: capturePayload,
          context,
          locationText,
          startedAt: Date.now(),
        };
        return {
          handled: true,
          message: pendingCapturePrompt(),
          highlightSection: context.section || "today-jarvis",
        };
      }

      const normalizedCapturePayload = normalizeVoiceText(capturePayload);
      const captureSemanticText = capturePayload !== raw
        && !/\b(error|errores|bug|bugs|falla|fallas|problema|problemas|backlog|fix|fixes|mejora|mejoras)\b/.test(normalizedCapturePayload)
        ? raw
        : capturePayload;
      const response = await ownerApi.createJarvisBacklogItem({
        priority: priorityFromText(captureSemanticText),
        title: titleFromUtterance(capturePayload),
        description: capturePayload,
        humanNote: `Capturado por Jarvis desde ${locationText}.`,
        sourceContext: context,
        conversationContext: ownerJarvisConversationContext(),
        captureKind: /\b(error|bug|falla|rompe|no funciona)\b/.test(normalizeVoiceText(captureSemanticText)) ? "EXPLICIT_ISSUE" : "FEATURE_IDEA",
      });
      const item = response?.item || {};
      const label = item.externalId || item.localRef || "el fix local";
      const pendingSync = item?.sync?.state !== "synced";
      return {
        handled: true,
        message: pendingSync
          ? `Anoté ${label} desde ${locationText}. Quedó guardado localmente y pendiente de sincronizar con Google Sheets; no se pierde aunque la conexión esté caída.`
          : `Anoté ${label} desde ${locationText} y ya quedó sincronizado con Google Sheets.`,
        highlightSection: context.section || "today-jarvis",
      };
    },
  };
}
